/**
 * Однострочное текстовое поле.
 *
 * Заводится по той же причине, что и `Select`: нативный `<input>` рисуется
 * системой, а не продуктом, и каждое место, где он остался, одевалось своим
 * классом — `verification.module.css:.control`, `works.module.css:.signInInput`,
 * `agrocycle.module.css:.reasonInput`. Три копии одного поля расходятся молча:
 * одна получает видимый фокус, вторая — нет, и клавиатурный человек теряет
 * курсор ровно на форме суждения.
 *
 * Второе, и более важное: у нативного поля нет места для ОБЪЯСНЕНИЯ. Причина
 * суждения, логин и причина блокировки слота — все три требуют сказать, куда
 * текст попадёт и что с ним будет дальше; в подпись это не помещается, и на
 * экранах объяснение уезжало в `title=`, откуда его не читает ни один
 * скринридер по своей воле.
 *
 * Поведение — React Aria: связка label ↔ input ↔ description по id,
 * `aria-required`, `data-disabled`. Собственной клавиатуры здесь нет.
 */

import type { ReactNode } from "react";
import {
  Input,
  Label,
  Text,
  TextField as AriaTextField,
} from "react-aria-components";
import { useT } from "../i18n/hooks";
import { Explained, FIELD_TEXT } from "./InfoPopover";
import styles from "./controls.module.css";

export interface TextFieldProps {
  /** Видимая подпись. Без неё обязателен `aria-label`. */
  label?: ReactNode;
  "aria-label"?: string;
  /** Объяснение подписи. Открывается нажатием, а не наведением. */
  explain?: ReactNode;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** Строка под полем: ограничение или последствие, а не повтор подписи. */
  description?: ReactNode;
  /** Тип ввода. `password` — единственная причина отличать его от текста. */
  type?: "text" | "password";
  /** Подсказка браузеру о заполнении. Пароль без неё не подставляется. */
  autoComplete?: string;
  isRequired?: boolean;
  isDisabled?: boolean;
  size?: "sm" | "md";
  className?: string;
}

export function TextField({
  label,
  "aria-label": ariaLabel,
  explain,
  value,
  onChange,
  placeholder,
  description,
  type = "text",
  autoComplete,
  isRequired,
  isDisabled,
  size = "md",
  className,
}: TextFieldProps) {
  const t = useT();
  return (
    <AriaTextField
      className={`${styles.field} ${size === "sm" ? styles.sizeSm : ""} ${className ?? ""}`}
      value={value}
      onChange={onChange}
      type={type}
      isRequired={isRequired}
      isDisabled={isDisabled}
      aria-label={label ? undefined : ariaLabel}
    >
      {label ? (
        <Label className={styles.label}>
          <Explained
            label={typeof label === "string" ? label : (ariaLabel ?? t(FIELD_TEXT.valueFallback))}
            explain={explain}
          >
            {label}
          </Explained>
        </Label>
      ) : null}

      <div className={styles.inputShell}>
        <Input
          className={styles.input}
          placeholder={placeholder}
          autoComplete={autoComplete}
        />
      </div>

      {description ? (
        <Text slot="description" className={styles.description}>
          {description}
        </Text>
      ) : null}
    </AriaTextField>
  );
}
